<template>
    <div
        ref="search-container"
        :class="[$style.wrapper, isSearchOpened && $style.search_opened]"
        @click="onOpen"
    >
        <SearchInput
            ref="search-input"
            :placeholder="$tr('search.placeholder')"
            :buttonText="$tr('search.placeholder')"
            :short="!isSearchOpened"
            @update:modelValue="onUpdate"
            @focus="onOpen"
            @enter="onEnter"
        />
        <div
            v-if="isSearchOpened"
            data-overlay-blur
            :class="$style.results_anchor"
        >
            <SearchResults
                :searchResults="searchData"
                :status
                :onClick="onItemClick"
            />
        </div>
    </div>
</template>
<script setup lang="ts">
import { onClickOutside, onKeyStroke } from '@vueuse/core'
import { useTemplateRef } from 'vue'
import { useRouter } from 'vue-router'

import { ROUTE_NAMES } from '@/router/constants'
import { SSR_QUERY } from '@/router/query-params'

import SearchInput from './components/SearchInput.vue'
import SearchResults from './components/SearchResults.vue'
import { useSearch } from './composables/use-search'

const container = useTemplateRef('search-container')
const searchInput = useTemplateRef('search-input')
const emit = defineEmits<{ open: []; close: [] }>()

const router = useRouter()
const { isSearchOpened, searchData, status, handleOpen, handleClose, onUpdate, onItemClick } = useSearch()

function onOpen() {
    handleOpen()
    emit('open')
    searchInput.value?.focus()
}

function onClose() {
    handleClose()
    emit('close')
}

function onEnter(query: string) {
    router.push({
        name: ROUTE_NAMES.SEARCH,
        query: { [SSR_QUERY.SEARCH_QUERY]: query },
    })
}

onClickOutside(container, () => {
    if (isSearchOpened.value) {
        onClose()
    }
})

onKeyStroke('Escape', () => {
    if (isSearchOpened.value) {
        const activeElement = document.activeElement as HTMLElement
        onClose()
        activeElement.blur()
    }
})

router.afterEach(onClose)
</script>
<style module>
.wrapper {
    transition: all 200ms ease-out;
    cursor: text;
}

.search_opened {
    z-index: 1;
    background-color: var(--black);
    position: relative;
    width: auto;
    height: 56px;
    flex: 1;
}

.results_anchor {
    width: 100%;
    padding-bottom: 12px;
    position: absolute;
    overflow: auto;
    scrollbar-width: none;
    margin-top: 12px;
    border-radius: 16px;
    z-index: 1;
    max-height: calc(100vh - 84px);
}
</style>
